import {
  ANNUAL_APPROVED_COLOR,
  ANNUAL_DRAFT_COLOR,
  ANNUAL_NEW_COLOR,
  ANNUAL_PLAN_STATUS_LEGEND,
  ANNUAL_REJECTED_COLOR,
  ANNUAL_SUBMITTED_COLOR,
  NA_COLOR,
  NO_DATA_COLOR,
} from '../omnidashboard.config';
import {
  AnnualPlanMatrixStatus,
  AnnualPlanRawStatus,
  AnnualPlanStatusEntry,
  MatrixCellData,
  MetricData,
  MetricSummaryBucket,
  OmniMetricDefinition,
} from '../omnidashboard.models';

const DEFAULT_NA_METRIC: MetricData = {
  displayValue: 'N/A',
  color: NA_COLOR,
  value: null,
  signedCount: 0,
};

const ANNUAL_PLAN_STATUS_BUCKETS: MetricSummaryBucket[] = [
  { value: 'New',       label: 'New',       color: ANNUAL_NEW_COLOR,       iconClass: 'fa-circle-o', tooltip: 'Annual plan not yet started' },
  { value: 'Draft',     label: 'Draft',     color: ANNUAL_DRAFT_COLOR,     iconClass: 'fa-pencil',   tooltip: 'Annual plan is in draft' },
  { value: 'Submitted', label: 'Submitted', color: ANNUAL_SUBMITTED_COLOR, iconClass: 'fa-arrow-up', tooltip: 'Annual plan submitted for review' },
  { value: 'Approved',  label: 'Approved',  color: ANNUAL_APPROVED_COLOR,  iconClass: 'fa-check',    tooltip: 'Annual plan has been approved' },
  { value: 'Rejected',  label: 'Rejected',  color: ANNUAL_REJECTED_COLOR,  iconClass: 'fa-times',    tooltip: 'Annual plan was rejected' },
  { value: 'No Data',   label: 'No Data',   color: NO_DATA_COLOR,          iconClass: 'fa-minus',    tooltip: 'No data available for this chapter' },
];

const ANNUAL_STATUS_ICON = new Map<string, string>(
  ANNUAL_PLAN_STATUS_BUCKETS.map(b => [b.value, b.iconClass])
);

function statusIcon(label: string): string {
  return ANNUAL_STATUS_ICON.get(label) || '';
}

export function mapToAnnualPlanStatus(rawStatus: AnnualPlanRawStatus): AnnualPlanMatrixStatus {
  switch (rawStatus) {
    case 'APPROVED':
      return { statusLabel: 'Approved', displayValue: statusIcon('Approved'), color: ANNUAL_APPROVED_COLOR };
    case 'SUBMITTED':
      return { statusLabel: 'Submitted', displayValue: statusIcon('Submitted'), color: ANNUAL_SUBMITTED_COLOR };
    case 'DRAFT':
      return { statusLabel: 'Draft', displayValue: statusIcon('Draft'), color: ANNUAL_DRAFT_COLOR };
    case 'NEW':
      return { statusLabel: 'New', displayValue: statusIcon('New'), color: ANNUAL_NEW_COLOR };
    case 'REJECTED':
      return { statusLabel: 'Rejected', displayValue: statusIcon('Rejected'), color: ANNUAL_REJECTED_COLOR };
  }
}

export function buildAnnualPlanStatusMap(
  items: AnnualPlanStatusEntry[]
): Map<number, AnnualPlanMatrixStatus> {
  const statusMap = new Map<number, AnnualPlanMatrixStatus>();
  items.forEach((item) => {
    statusMap.set(item.chapterId, mapToAnnualPlanStatus(item.budgetStatus));
  });
  return statusMap;
}

const NO_DATA_STATUS: AnnualPlanMatrixStatus = {
  statusLabel: 'No Data',
  displayValue: statusIcon('No Data'),
  color: NO_DATA_COLOR,
};

export function resolveAnnualPlanMetric(
  statusMap: Map<number, AnnualPlanMatrixStatus>,
  cell: MatrixCellData
): MetricData {
  const status = statusMap.get(cell.chapterId) || NO_DATA_STATUS;
  return {
    displayValue: status.displayValue,
    color: status.color,
    value: status.statusLabel,
    signedCount: 0,
  };
}

export const ANNUAL_PLAN_STATUS_METRIC: OmniMetricDefinition = {
  key: 'annual-plan-status',
  label: 'Annual Plan Status',
  legend: ANNUAL_PLAN_STATUS_LEGEND,
  summaryBuckets: ANNUAL_PLAN_STATUS_BUCKETS,
  naMetric: DEFAULT_NA_METRIC,
  tooltipFormatter: (cell: MatrixCellData, metric: MetricData): string => {
    const name = `${cell.siteName} – ${cell.bpgName}`;
    if (!metric.value) {
      return name;
    }
    return `${name} · ${metric.value}`;
  },
};
